import { DEFAULT_LANGUAGE, initI18n, loadNamespace } from '@sazeo/i18n';
import { captureError, initRequestId } from '@sazeo/observability';
import { ErrorBoundary } from '@sazeo/ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';

import { App } from './App';
import { initTenantObservability } from './lib/observability';
import { bootTheme } from './lib/theme';

import './styles/globals.css';

// Conditionally load CJK font — only for zh-CN to avoid shipping 200 KB to all users.
const _bootLang = localStorage.getItem('i18nextLng') ?? 'ka';
if (_bootLang === 'zh-CN') {
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://fonts.googleapis.com';
  document.head.appendChild(preconnect);

  const font = document.createElement('link');
  font.rel = 'stylesheet';
  font.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;600&display=swap';
  document.head.appendChild(font);
}

/**
 * Boot order:
 *   1. Apply theme synchronously so we never flash the wrong palette.
 *   2. Initialise i18next with the three namespaces this app consumes today.
 *   3. Eagerly warm the default-language bundles so the first paint has
 *      real copy instead of raw keys.
 *   4. Mount React.
 *
 * `initI18n` is safe to call before translations are loaded — components
 * will re-render via react-i18next once `loadNamespace` resolves.
 */
bootTheme();
initTenantObservability();
initRequestId();

// Global JS error capture (must run after initTenantObservability so the reporter is ready)
window.addEventListener('error', (event) => {
  captureError(event.error ?? new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  captureError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)), {
    type: 'unhandled_promise_rejection',
  });
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

async function boot() {
  await initI18n({
    defaultLang: DEFAULT_LANGUAGE,
    namespaces: [
      'core.nav',
      'core.settings',
      'core.home',
      'core.feedback',
      'core.invites',
      'core.people',
      'core.documents',
      'core.search',
      'core.addons',
      'addons.attendance',
    ],
  });

  // Pre-load the default language so the first render isn't empty.
  // Failures are non-fatal (keys render as-is) — see loadNamespace docs.
  await Promise.allSettled([
    loadNamespace('core.nav', DEFAULT_LANGUAGE),
    loadNamespace('core.settings', DEFAULT_LANGUAGE),
    loadNamespace('core.home', DEFAULT_LANGUAGE),
    loadNamespace('core.feedback', DEFAULT_LANGUAGE),
    loadNamespace('core.invites', DEFAULT_LANGUAGE),
    loadNamespace('core.people', DEFAULT_LANGUAGE),
    loadNamespace('core.documents', DEFAULT_LANGUAGE),
    loadNamespace('core.search', DEFAULT_LANGUAGE),
    loadNamespace('core.addons', DEFAULT_LANGUAGE),
    loadNamespace('addons.attendance', DEFAULT_LANGUAGE),
  ]);

  const root = document.getElementById('root');
  if (!root) throw new Error('Root element #root not found in index.html');

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  );
}

void boot();
